<template>
  <div class="post-editor">
    <div v-if="editor" class="toolbar">
      <select
        class="toolbar-select"
        :value="currentBlock"
        @change="setBlock($event.target.value)"
        aria-label="Tipo de texto"
      >
        <option value="paragraph">Párrafo</option>
        <option value="h2">Título</option>
        <option value="h3">Subtítulo</option>
        <option value="blockquote">Cita</option>
      </select>
      <span class="separator"></span>
      <button type="button" title="Negritas" @click="run((c) => c.toggleBold())" :class="{ 'is-active': editor.isActive('bold') }"><strong>B</strong></button>
      <button type="button" title="Cursiva" @click="run((c) => c.toggleItalic())" :class="{ 'is-active': editor.isActive('italic') }"><em>I</em></button>
      <button type="button" title="Subrayado" @click="run((c) => c.toggleUnderline())" :class="{ 'is-active': editor.isActive('underline') }"><u>U</u></button>
      <span class="separator"></span>
      <button type="button" title="Lista" @click="run((c) => c.toggleBulletList())" :class="{ 'is-active': editor.isActive('bulletList') }">• Lista</button>
      <button type="button" title="Lista numerada" @click="run((c) => c.toggleOrderedList())" :class="{ 'is-active': editor.isActive('orderedList') }">1. Lista</button>
      <button type="button" title="Separador" @click="run((c) => c.setHorizontalRule())">—</button>
      <span class="separator"></span>
      <button type="button" title="Alinear a la izquierda" @click="run((c) => c.setTextAlign('left'))" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }">⇤</button>
      <button type="button" title="Centrar" @click="run((c) => c.setTextAlign('center'))" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }">☰</button>
      <span class="separator"></span>
      <button type="button" title="Enlace" @click="setLink" :class="{ 'is-active': editor.isActive('link') }">🔗 Enlace</button>
      <button type="button" title="Quitar enlace" @click="run((c) => c.unsetLink())" :disabled="!editor.isActive('link')">Quitar enlace</button>
      <span class="separator"></span>
      <button type="button" title="Insertar imagen" @click="$refs.imageInput.click()" :disabled="uploading">
        {{ uploading ? "Subiendo…" : "🖼️ Imagen" }}
      </button>
      <input ref="imageInput" type="file" accept="image/*" class="hidden" @change="onImagePicked" />
      <span class="separator"></span>
      <button type="button" title="Deshacer" @click="run((c) => c.undo())">↶</button>
      <button type="button" title="Rehacer" @click="run((c) => c.redo())">↷</button>
    </div>

    <editor-content :editor="editor" class="editor-content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import api from "@/api";

// Minimal image node: block-level <img src alt>. Kept local so the editor
// needs no extra package beyond what the mass-email editor already uses.
const InlineImage = Node.create({
  name: "image",
  group: "block",
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
  addCommands() {
    return {
      setImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export default {
  name: "PostEditor",

  components: { EditorContent },

  props: {
    modelValue: { type: String, default: "" },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      editor: null,
      uploading: false,
      currentBlock: "paragraph",
    };
  },

  watch: {
    modelValue(value) {
      if (!this.editor || this.editor.getHTML() === value) return;
      this.editor.commands.setContent(value, false);
    },
  },

  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({ heading: { levels: [2, 3] } }),
        Underline,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: "noopener noreferrer" },
        }),
        InlineImage,
      ],
      content: this.modelValue,
      onUpdate: () => this.$emit("update:modelValue", this.editor.getHTML()),
      onSelectionUpdate: () => this.syncBlock(),
      onTransaction: () => this.syncBlock(),
    });
  },

  beforeUnmount() {
    if (this.editor) this.editor.destroy();
  },

  methods: {
    run(fn) {
      fn(this.editor.chain().focus()).run();
    },

    syncBlock() {
      if (!this.editor) return;
      if (this.editor.isActive("heading", { level: 2 })) this.currentBlock = "h2";
      else if (this.editor.isActive("heading", { level: 3 })) this.currentBlock = "h3";
      else if (this.editor.isActive("blockquote")) this.currentBlock = "blockquote";
      else this.currentBlock = "paragraph";
    },

    setBlock(value) {
      const chain = this.editor.chain().focus();
      if (value === "h2") chain.setHeading({ level: 2 }).run();
      else if (value === "h3") chain.setHeading({ level: 3 }).run();
      else if (value === "blockquote") chain.setParagraph().setBlockquote().run();
      else chain.setParagraph().unsetBlockquote().run();
    },

    setLink() {
      const previous = this.editor.getAttributes("link").href;
      const url = window.prompt("URL del enlace:", previous || "https://");
      if (url === null) return;
      if (url === "" || url === "https://") {
        this.run((c) => c.extendMarkRange("link").unsetLink());
        return;
      }
      this.run((c) => c.extendMarkRange("link").setLink({ href: url }));
    },

    async onImagePicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      this.uploading = true;
      try {
        const response = await api.posts.uploadInlineImage(file);
        const alt = window.prompt("Texto alternativo de la imagen (opcional):", "") || null;
        this.run((c) => c.setImage({ src: response.url, alt }));
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "No se pudo subir la imagen.");
      }
      this.uploading = false;
    },
  },
};
</script>

<style scoped>
.post-editor {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
}

.toolbar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0.5rem 0.5rem 0 0;
}

.toolbar button,
.toolbar-select {
  padding: 0.375rem 0.6rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.8rem;
  line-height: 1.2;
}

.toolbar button:hover {
  background: #f3f4f6;
}

.toolbar button.is-active {
  background: #5e3a9a;
  color: white;
  border-color: #5e3a9a;
}

.toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.separator {
  width: 1px;
  height: 1.5rem;
  background: #d1d5db;
  margin: 0 0.25rem;
}

.editor-content {
  padding: 1rem 1.25rem;
  min-height: 420px;
}

.editor-content :deep(.ProseMirror) {
  outline: none;
  min-height: 400px;
  font-size: 1rem;
  line-height: 1.7;
  color: #1f2937;
}

.editor-content :deep(.ProseMirror p) {
  margin: 0.6rem 0;
}

.editor-content :deep(.ProseMirror h2) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1.5rem 0 0.5rem;
  color: #492a7d;
}

.editor-content :deep(.ProseMirror h3) {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 1.25rem 0 0.4rem;
  color: #492a7d;
}

.editor-content :deep(.ProseMirror ul),
.editor-content :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.editor-content :deep(.ProseMirror ul) {
  list-style: disc;
}

.editor-content :deep(.ProseMirror ol) {
  list-style: decimal;
}

.editor-content :deep(.ProseMirror blockquote) {
  border-left: 4px solid #c4a8d8;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #4b5563;
  font-style: italic;
}

.editor-content :deep(.ProseMirror hr) {
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 1.5rem 0;
}

.editor-content :deep(.ProseMirror a) {
  color: #5e3a9a;
  text-decoration: underline;
}

.editor-content :deep(.ProseMirror img) {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1rem auto;
}

.editor-content :deep(.ProseMirror img.ProseMirror-selectednode) {
  outline: 3px solid #5e3a9a;
}
</style>
